import { Outlet } from 'react-router';

import { useSession } from '@/features/auth/useSession';
import { isManager, isPlatform } from '@/shared/session/currentUser';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * Guards for the screens only an administrator sees.
 *
 * They hang inside `RequireAuth`, so by the time they run there is a session; what they
 * add is the tier (contract v10.11). `RequireAdmin` is the management half of the panel:
 * Ignira, or anybody with the `is_admin` switch on. `RequirePlatform` is the provider's
 * half (statistics, access log, retention, document policy): Ignira alone.
 *
 * **A convenience, not the control**: the API answers 403 to the wrong tier whatever
 * these components do, and that is what actually protects the real addresses the
 * activation panel shows. Hiding a screen that the server would refuse anyway is for the
 * user's sake, not for security's, and treating it as security is how somebody ends up
 * removing the server-side check because "the front already hides it".
 *
 * The refusal names nobody and no command: who administers the application is a fact of
 * the shop, not of the screen.
 */
export function RequireAdmin() {
  const session = useSession();

  if (session.isPending) {
    return <Spinner variant="fill" label="Comprobando permisos" />;
  }

  if (!isManager(session.data)) {
    return (
      <EmptyState
        title="Esta pantalla es solo para administradores"
        hint="Pídele acceso a quien administra la aplicación."
      />
    );
  }

  return <Outlet />;
}

export function RequirePlatform() {
  const session = useSession();

  if (session.isPending) {
    return <Spinner variant="fill" label="Comprobando permisos" />;
  }

  if (!isPlatform(session.data)) {
    return (
      <EmptyState
        title="Esta pantalla es solo para superadministradores"
        hint="La lleva quien mantiene la aplicación, no la ferretería."
      />
    );
  }

  return <Outlet />;
}
